import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [perms, setPerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [env, setEnvState] = useState(() => localStorage.getItem("pte_env") || "test");
  const [canAccessLive, setCanAccessLive] = useState(false);
  const [toggleEnabled, setToggleEnabled] = useState(true);

  const loadPerms = useCallback(async () => {
    try {
      const { data } = await api.get("/me/permissions");
      setPerms(data.modules || {});
    } catch {
      setPerms({});
    }
  }, []);

  const loadEnvStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/env/status");
      setCanAccessLive(!!data.can_access_live);
      setToggleEnabled(data.toggle_enabled !== false);
      // Server may force us back to "test" (e.g. a non-admin's stale "live"
      // preference from before a role change) — keep localStorage in sync.
      if (data.current_env !== localStorage.getItem("pte_env")) {
        localStorage.setItem("pte_env", data.current_env);
        setEnvState(data.current_env);
      }
    } catch {
      /* ignore — keep whatever we already had */
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("pte_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        await Promise.all([loadPerms(), loadEnvStatus()]);
      })
      .catch(() => {
        localStorage.removeItem("pte_token");
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, [loadPerms, loadEnvStatus]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return await _afterLogin(data);
    } catch (e) {
      return { ok: false, error: apiErr(e) };
    }
  };

  // Backend-driven demo sign-in — never touches real credentials from the
  // frontend (see routes_auth.py's DEMO_LOGIN_ENABLED flag / Login.js).
  const demoLogin = async (role) => {
    try {
      const { data } = await api.post("/auth/demo-login", { role });
      return await _afterLogin(data);
    } catch (e) {
      return { ok: false, error: apiErr(e) };
    }
  };

  const _afterLogin = async (data) => {
    localStorage.setItem("pte_token", data.token);
    // "Test" is only the *default* for a browser that has never chosen
    // before (see useState initializer above) — once someone has
    // deliberately switched to Live, that choice sticks across future
    // logins on this device. We deliberately do NOT force-reset to "test"
    // here: doing so previously caused a real incident where an admin's
    // earlier "Live" choice was silently discarded on a fresh login,
    // so a real staff edit landed in the Test sandbox instead of Live.
    const current = localStorage.getItem("pte_env") || "test";
    setEnvState(current);
    setUser(data.user);
    await Promise.all([loadPerms(), loadEnvStatus()]);
    return { ok: true, user: data.user };
  };

  const logout = () => {
    localStorage.removeItem("pte_token");
    // Deliberately keep "pte_env" — a device's Live/Test choice persists
    // across logout/login (only ever defaults to "test" the very first
    // time, or if the server forces it back down for a role change).
    setUser(false);
    setPerms(null);
    window.location.href = "/login";
  };

  const switchEnv = (next) => {
    if (next === "live" && !canAccessLive) return;
    localStorage.setItem("pte_env", next);
    setEnvState(next);
    // Simplest correct way to guarantee every open page refetches against
    // the newly-selected environment instead of showing stale cached data.
    window.location.reload();
  };

  const can = useCallback(
    (module, action = "view") => !!(perms && perms[module] && perms[module][action]),
    [perms]
  );

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, perms, loading, login, demoLogin, logout, can, refreshUser,
      env, canAccessLive, toggleEnabled, switchEnv, isCustomer: user?.user_type === "customer" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
